import { XCircleIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useMemo, useState } from 'react'
import photoImg from '../../assets/photo-img.png'

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function truncateMiddle(name: string, max = 24): string {
  if (name.length <= max) return name
  const dot = name.lastIndexOf('.')
  const ext = dot >= 0 ? name.slice(dot) : ''
  const base = dot >= 0 ? name.slice(0, dot) : name
  if (base.length <= 4) return name
  const head = base.slice(0, Math.max(4, Math.floor((max - ext.length - 3) / 2)))
  const tail = base.slice(-Math.max(3, Math.ceil((max - ext.length - 3) / 2)))
  return `${head}...${tail}${ext}`
}

const TILE_SIZE = 100

const PdfIconSvg = () => (
  <svg width="60" height="72" viewBox="0 0 80 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8 4 H50 L72 26 V88 a4 4 0 0 1 -4 4 H8 a4 4 0 0 1 -4 -4 V8 a4 4 0 0 1 4 -4 Z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
    <path d="M50 4 V22 a4 4 0 0 0 4 4 H72 Z" fill="#e5e7eb" stroke="#cbd5e1" strokeWidth="1.5" strokeLinejoin="round" />
    <rect x="14" y="46" width="48" height="20" rx="3" fill="#dc2626" />
    <text x="38" y="61" fontFamily="Arial, Helvetica, sans-serif" fontSize="13" fontWeight="800" fill="#ffffff" textAnchor="middle" letterSpacing="1">PDF</text>
  </svg>
)

const AttachmentMeta = ({ name, size }: { name: string; size?: number }) => (
  <div style={{ width: '100%', textAlign: 'left' }}>
    <p
      title={name}
      style={{
        margin: 0,
        fontSize: 12,
        fontWeight: 500,
        color: '#212121',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1.3,
      }}
    >
      {truncateMiddle(name, 16)}
    </p>
    {!!size && (
      <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', lineHeight: 1.3 }}>{formatBytes(size)}</p>
    )}
  </div>
)
import './index.scss'
import { AttachmentEntity } from '../../customEntites/Attachment.entity'
import LoadingOverlay from '../LoadingOverlay'
import { useTranslation } from 'react-i18next'

const ImagePreview = ({
  attachments,
  uploader,
  download,
  remove,
  setAttachments
}: {
  attachments: AttachmentEntity[],
  uploader?: any,
  download?: boolean | undefined,
  remove?: boolean | undefined,
  setAttachments?: any
}) => {
  const { t } = useTranslation()
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  // Build a stable blob URL per file so re-renders don't recreate the URL
  // and force <img> to reload on every render.
  const imageSrcs = useMemo(() => {
    return attachments.map((file: AttachmentEntity) => {
      const actualFile: any = file?.file || file
      const isPDF = actualFile?.type === 'application/pdf'
      const isUploading = file?.customProperties?.uploading
      if (isPDF) return '' // rendered via <PdfTile />, no <img> src needed
      if (isUploading) return photoImg
      return URL.createObjectURL(actualFile)
    })
  }, [attachments])

  // Build a real blob URL per file (regardless of type) for the modal preview/download.
  const fileUrls = useMemo(() => {
    return attachments.map((file: AttachmentEntity) => {
      const actualFile: any = file?.file || file
      if (file?.customProperties?.uploading) return ''
      return URL.createObjectURL(actualFile)
    })
  }, [attachments])

  // Revoke blob URLs when the list changes or component unmounts.
  useEffect(() => {
    return () => {
      imageSrcs.forEach((src) => {
        if (src.startsWith('blob:')) URL.revokeObjectURL(src)
      })
      fileUrls.forEach((src) => {
        if (src.startsWith('blob:')) URL.revokeObjectURL(src)
      })
    }
  }, [imageSrcs, fileUrls])

  // Close modal on Escape
  useEffect(() => {
    if (previewIndex === null) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreviewIndex(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewIndex])

  const onRemoveAttachment = (index: number) => {
    const newAttachments = [...attachments]
    newAttachments.splice(index, 1)
    setAttachments(newAttachments)
  }

  const downloadFile = (file: File, url: string) => {
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const previewFile: any = previewIndex !== null ? (attachments[previewIndex]?.file || attachments[previewIndex]) : null
  const previewUrl = previewIndex !== null ? fileUrls[previewIndex] : ''
  const isPreviewPdf = previewFile?.type === 'application/pdf'

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%' }} role='list'>
        {attachments.map((file: AttachmentEntity, index: number) => {
          const actualFile: any = file?.file || file
          const isDownload = download && !file?.customProperties?.uploading
          const isUploading = file?.customProperties?.uploading
          const isPDF = actualFile?.type === 'application/pdf'
          const imageSrc = imageSrcs[index]

          const card = (
            <div
              style={{
                width: TILE_SIZE,
                height: TILE_SIZE,
                background: '#f3f4f6',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {isUploading && (
                <LoadingOverlay uploading={true} position='absolute' />
              )}
              {isPDF ? (
                <PdfIconSvg />
              ) : (
                <img
                  src={imageSrc}
                  alt=""
                  aria-hidden="true"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </div>
          )

          return (
            <div
              key={index}
              role='listitem'
              style={{ width: TILE_SIZE, display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}
            >
              {remove && (
                <button
                  type='button'
                  onClick={() => onRemoveAttachment(index)}
                  className='delete-attachment-button'
                  aria-label={`${t('BUTTON.DELETE')} ${actualFile.name}`}
                >
                  <XCircleIcon aria-hidden='true' title='Delete Attachment' />
                </button>
              )}
              {isDownload ? (
                <button
                  type='button'
                  onClick={() => setPreviewIndex(index)}
                  aria-label={`${t('BUTTON.OPEN') || 'Open'} ${actualFile.name}`}
                  style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer', width: TILE_SIZE }}
                >
                  {card}
                </button>
              ) : (
                card
              )}
              <AttachmentMeta name={actualFile.name} size={actualFile.size} />
            </div>
          )
        })}

        {uploader && (
          <div role="listitem">
            {uploader}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewIndex !== null && previewFile && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewIndex(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: isPreviewPdf ? '90vw' : 'auto',
              height: isPreviewPdf ? '90vh' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid #e5e7eb',
                gap: 12,
              }}
            >
              <span
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#212121',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {previewFile.name}
              </span>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => downloadFile(previewFile, previewUrl)}
                  aria-label={String(t('BUTTON.DOWNLOAD') || 'Download')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 6,
                    background: '#218cda',
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
                  {t('BUTTON.DOWNLOAD') || 'Download'}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewIndex(null)}
                  aria-label={String(t('BUTTON.CLOSE') || 'Close')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <XMarkIcon style={{ width: 18, height: 18 }} />
                </button>
              </div>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
                background: isPreviewPdf ? '#525659' : '#fff',
                padding: isPreviewPdf ? 0 : 16,
              }}
            >
              {isPreviewPdf ? (
                <iframe
                  src={previewUrl}
                  title={previewFile.name}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={previewFile.name}
                  style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ImagePreview
